<template>
  <v-dialog
    v-model="opened"
    content-class="company-expenses-dialog"
    max-width="350"
    persistent
  >
    <h3 class="company-expenses-dialog__title">
      {{ $t('companyExpenses.add') }}
    </h3>

    <v-btn class="close-btn" icon @click="opened = false">
      <v-icon size="27" color="#464646"> mdi-close-circle-outline </v-icon>
    </v-btn>

    <v-form
      ref="expenses-dialog"
      class="company-expenses-dialog__form"
      @submit.prevent="onSubmit"
    >
      <menu-datepicker
        dense
        outlined
        :value="formData.date"
        :rules="[isRequired]"
        @change="formData.date = $event"
      />
      <expense-type-select
        v-model="formData.finance_type_id"
        :rules="[isRequired]"
        dense
        outlined
        clearable
      />
      <type-category-select
        v-model="formData.finance_type_category_id"
        :type-id="formData.finance_type_id"
        :rules="[isRequired]"
        dense
        outlined
      />
      <v-select
        v-model="formData.cityId"
        item-text="name"
        item-value="id"
        dense
        outlined
        :label="$t('finance.city')"
        :items="citiesList"
        :rules="[isRequired]"
      />
      <v-select
        v-model="formData.office_id"
        item-text="name"
        item-value="id"
        dense
        outlined
        :label="$t('finance.office')"
        :disabled="!formData.cityId"
        :items="officesList"
        :rules="[isRequired]"
      />
      <v-text-field
        v-model="formData.description"
        dense
        outlined
        :label="$t('common.table.description')"
      />
      <v-text-field
        v-model="formData.amount"
        dense
        outlined
        :label="$t('common.fields.sum')"
        :rules="[isRequired, onlyNumbers]"
      />

      <v-btn
        elevation="0"
        type="submit"
        color="warning"
        block
        :loading="loading"
      >
        {{ $t('common.btns.save') }}
      </v-btn>
    </v-form>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import MenuDatepicker from '@/components/shared/MenuDatepicker.vue';
import ExpenseTypeSelect from './ExpenseTypeSelect.vue';
import TypeCategorySelect from './TypeCategorySelect.vue';

export default {
  components: { MenuDatepicker, ExpenseTypeSelect, TypeCategorySelect },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,

    typeInput: '',
    categoryInput: '',
    officeInput: '',

    typeLoading: false,
    categoryLoading: false,
    officeLoading: false,

    formData: {
      date: '',
      finance_type_id: '',
      finance_type_category_id: '',
      cityId: '',
      office_id: '',
      description: '',
      amount: '',
    },
  }),
  watch: {
    opened(value) {
      if (value) {
        this.formData.date = this.today;
        this.fetchCities();
      } else {
        this.$refs['expenses-dialog'].reset();
      }
    },
    typeInput(title) {
      this.fetchTypesList(title);
    },
    categoryInput(title) {
      this.fetchCategories(title);
    },
    'formData.finance_type_id': {
      handler() {
        this.formData.finance_type_category_id = '';
      },
    },
  },
  computed: {
    ...mapState({
      typesList: (state) => state['company-expenses'].expenseTypesList,
      categoriesList: (state) => state['company-expenses'].categoriesList,
      citiesList: (state) => state['company-expenses'].citiesList,
    }),
    opened: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('change', val);
      },
    },
    today() {
      const date = new Date();
      const formatNum = (v) => (v < 10 ? `0${v}` : v);

      return `${date.getFullYear()}-${formatNum(
        date.getMonth() + 1,
      )}-${date.getDate()}`;
    },
    officesList() {
      if (!this.formData.cityId) return [];

      const checkedCity = this.citiesList.find(
        ({ id }) => id === this.formData.cityId,
      );

      return checkedCity.offices;
    },
  },
  methods: {
    isRequired(v) {
      return !!v || this.$t('common.fields.validation.field.required');
    },
    onlyNumbers(v) {
      if (!v) return true;
      return (
        /^[0-9]{1,12}$/.test(v) || this.$t('common.fields.validation.numbers')
      );
    },
    async fetchTypesList(title) {
      this.typeLoading = true;
      await this.$store.dispatch('company-expenses/fetchExpenseTypes', {
        title,
      });
      this.typeLoading = false;
    },
    async fetchCategories(title) {
      if (!this.formData.finance_type_id) return;

      this.categoryLoading = true;
      await this.$store.dispatch('company-expenses/fetchCategories', {
        typeId: this.formData.finance_type_id,
        title,
      });
      this.categoryLoading = false;
    },
    async fetchCities(title = '') {
      this.typeLoading = true;
      await this.$store.dispatch('company-expenses/fetchCities', title);
      this.typeLoading = false;
    },
    async onSubmit() {
      const isValid = this.$refs['expenses-dialog'].validate();

      if (!isValid) return;

      this.loading = true;

      const result = await this.$store.dispatch(
        'company-expenses/addNew',
        this.formData,
      );

      this.$emit('updated', result);
      this.loading = false;
    },
  },
};
</script>

<style lang="scss">
.company-expenses-dialog {
  padding: 10px;
  background-color: #fff;

  &__title {
    font-size: 18px;
    color: #464646;
  }

  &__form {
    margin-top: 15px;
  }
}
</style>
